import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Code2, Eye, Info } from "lucide-react";

import {
  useCreateEmailLayoutMutation,
  useLazyGetEmailLayoutByIdQuery,
  useUpdateEmailLayoutMutation,
  useLazyGetEmailBrandingProfilesQuery,
  useLazyGetEmailBrandingProfileByIdQuery,
} from "../../../redux/actions/Admin/ServiceAdmin";

import {
  ICreateEmailLayoutRequest,
  IUpdateEmailLayoutRequest,
} from "../../../types/Request/AdminRequest";

import { useToast } from "../../../utils/useToast";
import ToastContainer from "../../../utils/ToastContainer";

const NAVIGATE_TARGET = "/admin/adminemailtemplates/layouts";

const EmailLayoutDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isEdit = Boolean(id);
  const DEFAULT_HEADER_HTML = `
<div style="padding:20px;text-align:center;">
  <img
    src="{{LogoUrl}}"
    alt="{{CompanyName}}"
    style="max-width:180px;height:auto;"
  />
</div>
`;

  const DEFAULT_FOOTER_HTML = `
<hr />

<div style="font-size:12px;text-align:center;">
  <p>{{CompanyName}}</p>

  <p>{{SupportEmail}}</p>

  <p>{{CopyrightText}}</p>

  <p>
    <a href="{{TermsUrl}}">Terms</a>
    |
    <a href="{{PrivacyPolicyUrl}}">Privacy</a>
  </p>
</div>
`;
  const [getEmailLayoutById, { data: layout, isFetching, isError }] =
    useLazyGetEmailLayoutByIdQuery();

  const [
    getBrandingProfiles,
    { data: brandingProfiles, isFetching: isBrandingLoading },
  ] = useLazyGetEmailBrandingProfilesQuery();
  const [getBrandingProfileById, { data: brandingProfileDetails }] =
    useLazyGetEmailBrandingProfileByIdQuery();
  const [createEmailLayout, { isLoading: isCreating }] =
    useCreateEmailLayoutMutation();

  const [updateEmailLayout, { isLoading: isUpdating }] =
    useUpdateEmailLayoutMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [headerHtml, setHeaderHtml] = useState(DEFAULT_HEADER_HTML);

  const [footerHtml, setFooterHtml] = useState(DEFAULT_FOOTER_HTML);
  const [brandingProfileId, setBrandingProfileId] = useState<number | "">("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [showHeaderPreview, setShowHeaderPreview] = useState(false);

  const [showFooterPreview, setShowFooterPreview] = useState(false);

  const isSubmitting = isCreating || isUpdating;
  const { toasts, addToast, removeToast } = useToast();
  // --------------------------------------------------
  // Load layout
  // --------------------------------------------------

  useEffect(() => {
    if (isEdit && id) {
      getEmailLayoutById(Number(id));
    }
  }, [id, isEdit, getEmailLayoutById]);

  // --------------------------------------------------
  // Load branding profiles
  // --------------------------------------------------

  useEffect(() => {
    getBrandingProfiles();
  }, [getBrandingProfiles]);

  // --------------------------------------------------
  // Load selected branding profile details
  // --------------------------------------------------

  useEffect(() => {
    if (brandingProfileId === "") {
      return;
    }

    getBrandingProfileById(Number(brandingProfileId));
  }, [brandingProfileId, getBrandingProfileById]);
  // --------------------------------------------------
  // Populate form
  // --------------------------------------------------

  useEffect(() => {
    if (!layout) {
      return;
    }

    setName(layout.name ?? "");
    setDescription(layout.description ?? "");
    setHeaderHtml(layout.headerHtml ?? "");
    setFooterHtml(layout.footerHtml ?? "");

    setBrandingProfileId(layout.brandingProfileId ?? "");

    setIsDefault(layout.isDefault);
    setIsActive(layout.isActive);
  }, [layout]);

  // --------------------------------------------------
  // Default branding profile
  // --------------------------------------------------

  useEffect(() => {
    if (
      !isEdit &&
      brandingProfiles &&
      brandingProfiles.length > 0 &&
      brandingProfileId === ""
    ) {
      const defaultProfile = brandingProfiles.find((item) => item.isDefault);

      if (defaultProfile) {
        setBrandingProfileId(defaultProfile.id);
      }
    }
  }, [brandingProfiles, brandingProfileId, isEdit]);

  // --------------------------------------------------
  // Selected branding
  // --------------------------------------------------

  const selectedBranding = useMemo(() => {
    if (brandingProfileId === "") {
      return null;
    }

    return brandingProfileDetails ?? null;
  }, [brandingProfileId, brandingProfileDetails]);

  const renderPreviewHtml = (html: string): string => {
    if (!html) {
      return "";
    }

    if (!selectedBranding) {
      return html;
    }

    const variables: Record<string, string> = {
      LogoUrl: selectedBranding.logoUrl ?? "",
      CompanyName: selectedBranding.companyName ?? "",
      TagLine: selectedBranding.tagLine ?? "",
      WebsiteUrl: selectedBranding.websiteUrl ?? "",
      SupportEmail: selectedBranding.supportEmail ?? "",
      SupportPhone: selectedBranding.supportPhone ?? "",
      Address: selectedBranding.address ?? "",

      // Support both existing and new placeholder names
      Copyright: selectedBranding.copyrightText ?? "",
      CopyrightText: selectedBranding.copyrightText ?? "",

      TermsUrl: selectedBranding.termsUrl ?? "",
      PrivacyPolicyUrl: selectedBranding.privacyPolicyUrl ?? "",
      FacebookUrl: selectedBranding.facebookUrl ?? "",
      InstagramUrl: selectedBranding.instagramUrl ?? "",
      TwitterUrl: selectedBranding.twitterUrl ?? "",
      LinkedInUrl: selectedBranding.linkedInUrl ?? "",
      YouTubeUrl: selectedBranding.youTubeUrl ?? "",
    };

    let result = html;

    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, "g"), value);
    });

    return result;
  };

  // --------------------------------------------------
  // Navigation
  // --------------------------------------------------

  const handleBack = () => {
    navigate(NAVIGATE_TARGET);
  };

  // --------------------------------------------------
  // Save
  // --------------------------------------------------

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      addToast("error", "Invalid Input", "Please enter a layout name.");
      return;
    }

    if (!brandingProfileId) {
      addToast("error", "Invalid Input", "Please select a branding profile.");
      return;
    }

    if (!headerHtml.trim()) {
      addToast("error", "Invalid Input", "Header HTML is required.");
      return;
    }

    if (!footerHtml.trim()) {
      addToast("error", "Invalid Input", "Footer HTML is required.");
      return;
    }

    try {
      if (isEdit && layout) {
        const data: IUpdateEmailLayoutRequest = {
          name: name.trim(),
          description: description.trim(),
          headerHtml,
          footerHtml,
          brandingProfileId: Number(brandingProfileId),
          isActive,
        };

        await updateEmailLayout({
          id: layout.id,
          data,
        }).unwrap();

        addToast(
          "success",
          "Layout Updated",
          "Email layout updated successfully.",
        );

        setTimeout(() => {
          navigate(NAVIGATE_TARGET);
        }, 800);
      } else {
        const data: ICreateEmailLayoutRequest = {
          name: name.trim(),
          description: description.trim(),
          headerHtml,
          footerHtml,
          brandingProfileId: Number(brandingProfileId),
          isDefault,
          isActive,
        };

        await createEmailLayout(data).unwrap();
        addToast(
          "success",
          "Layout Created",
          "Email layout created successfully.",
        );

        setTimeout(() => {
          navigate(NAVIGATE_TARGET);
        }, 800);
      }
    } catch (error: any) {
      console.error("Email layout save failed:", error);
      const message =
        error?.data?.Message ||
        error?.data?.message ||
        "Failed to save email layout.";

      addToast("error", "Save Failed", message);
    }
  };

  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (isEdit && isFetching && !layout) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-primary" />

          <p className="text-sm text-slate-500">Loading email layout...</p>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // Error
  // --------------------------------------------------

  if (isEdit && (isError || (!isFetching && !layout))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">
            Email Layout Not Found
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            The email layout you are trying to edit could not be found.
          </p>

          <button
            type="button"
            onClick={handleBack}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#2E7D32] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#256B2A]"
          >
            <ArrowLeft size={16} />
            Back to Layouts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      {/* ------------------------------------------------ */}
      {/* HEADER */}
      {/* ------------------------------------------------ */}

      <div className="border-b border-slate-200 bg-white">
        <div className="px-6 py-5 sm:px-8">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              className="group flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
              title="Back to Layouts"
            >
              <ArrowLeft
                size={18}
                className="transition-transform group-hover:-translate-x-0.5"
              />
            </button>

            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {isEdit ? "Edit Email Layout" : "Create Email Layout"}
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                Configure the reusable structure, branding and HTML used by
                email templates.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------ */}
      {/* CONTENT */}
      {/* ------------------------------------------------ */}

      <div className="p-6 sm:p-8">
        <form onSubmit={handleSave} className="mx-auto max-w-7xl">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {/* Accent */}

            <div className="h-1.5 w-full bg-gradient-to-r from-primary via-indigo-500 to-sky-500" />

            <div className="space-y-8 p-6 sm:p-8">
              {/* ------------------------------------------------ */}
              {/* BASIC INFORMATION */}
              {/* ------------------------------------------------ */}

              <section>
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-slate-900">
                    Layout Information
                  </h2>

                  <p className="mt-1 text-xs text-slate-500">
                    Basic information and branding configuration.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {/* Name */}

                  <div>
                    <label className="mb-2 block text-xs font-medium text-slate-700">
                      Layout Name
                      <span className="ml-1 text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Default"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10"
                    />
                  </div>

                  {/* Branding */}

                  <div>
                    <label className="mb-2 block text-xs font-medium text-slate-700">
                      Branding Profile
                      <span className="ml-1 text-red-500">*</span>
                    </label>

                    <select
                      value={brandingProfileId}
                      onChange={(e) =>
                        setBrandingProfileId(
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                      disabled={
                        brandingProfileId === "" &&
                        brandingProfiles === undefined
                      }
                      className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
                    >
                      <option value="">
                        {isBrandingLoading
                          ? "Loading branding profiles..."
                          : "Select branding profile"}
                      </option>

                      {brandingProfiles?.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                          {profile.isDefault ? " (Default)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-medium text-slate-700">
                      Description
                    </label>

                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="Describe the purpose of this email layout..."
                      className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/10"
                    />
                  </div>
                </div>

                {/* Selected branding preview */}

                {selectedBranding && (
                  <div className="mt-5 flex items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-slate-200 bg-white">
                      {selectedBranding.logoUrl ? (
                        <img
                          src={selectedBranding.logoUrl}
                          alt={selectedBranding.companyName}
                          className="max-h-10 max-w-[48px] object-contain"
                        />
                      ) : (
                        <span className="text-xs text-slate-400">No Logo</span>
                      )}
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        {selectedBranding.companyName}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500">
                        {selectedBranding.tagLine}
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* ------------------------------------------------ */}
              {/* STATUS */}
              {/* ------------------------------------------------ */}

              <section className="border-t border-slate-100 pt-7">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Active */}

                  <label className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Active
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        Allow this layout to be used by email templates.
                      </p>
                    </div>

                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="h-4 w-4 accent-[#2E7D32]"
                    />
                  </label>

                  {/* Default - Create only */}

                  {!isEdit && (
                    <label className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          Make Default
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          Use this as the default layout for new templates.
                        </p>
                      </div>

                      <input
                        type="checkbox"
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                        className="h-4 w-4 accent-[#2E7D32]"
                      />
                    </label>
                  )}
                </div>
              </section>

              {/* ------------------------------------------------ */}
              {/* HTML INFORMATION */}
              {/* ------------------------------------------------ */}

              <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
                <Info size={17} className="mt-0.5 shrink-0 text-blue-600" />

                <div className="text-xs text-blue-700">
                  <p className="font-semibold">Available placeholders</p>

                  <p className="mt-1 leading-5">
                    You can use dynamic placeholders such as{" "}
                    <code>{"{{LogoUrl}}"}</code>,{" "}
                    <code>{"{{CompanyName}}"}</code>,{" "}
                    <code>{"{{SupportEmail}}"}</code>,{" "}
                    <code>{"{{CopyrightText}}"}</code>,{" "}
                    <code>{"{{TermsUrl}}"}</code>
                    and <code>{"{{PrivacyPolicyUrl}}"}</code>.
                  </p>
                </div>
              </div>

              {/* ------------------------------------------------ */}
              {/* HEADER HTML */}
              {/* ------------------------------------------------ */}

              <section>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Code2 size={17} className="text-primary" />

                      <h2 className="text-base font-semibold text-slate-900">
                        Header HTML
                      </h2>
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      HTML rendered above the email template content.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowHeaderPreview((value) => !value)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    <Eye size={14} />

                    {showHeaderPreview ? "Edit HTML" : "Preview"}
                  </button>
                </div>

                {showHeaderPreview ? (
                  <div className="min-h-[260px] overflow-auto rounded-xl border border-slate-200 bg-white p-4">
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          renderPreviewHtml(headerHtml) ||
                          "<p style='color:#94a3b8;text-align:center;padding:40px;'>No header HTML configured.</p>",
                      }}
                    />
                  </div>
                ) : (
                  <textarea
                    value={headerHtml}
                    onChange={(e) => setHeaderHtml(e.target.value)}
                    rows={12}
                    spellCheck={false}
                    placeholder={`<div style="padding:20px;text-align:center;">
  <img src="{{LogoUrl}}" />
</div>`}
                    className="w-full resize-y rounded-xl border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-xs leading-5 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/10"
                  />
                )}
              </section>

              {/* ------------------------------------------------ */}
              {/* FOOTER HTML */}
              {/* ------------------------------------------------ */}

              <section className="border-t border-slate-100 pt-7">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Code2 size={17} className="text-primary" />

                      <h2 className="text-base font-semibold text-slate-900">
                        Footer HTML
                      </h2>
                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                      HTML rendered below the email template content.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowFooterPreview((value) => !value)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    <Eye size={14} />

                    {showFooterPreview ? "Edit HTML" : "Preview"}
                  </button>
                </div>

                {showFooterPreview ? (
                  <div className="min-h-[260px] overflow-auto rounded-xl border border-slate-200 bg-white p-4">
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          renderPreviewHtml(footerHtml) ||
                          "<p style='color:#94a3b8;text-align:center;padding:40px;'>No footer HTML configured.</p>",
                      }}
                    />
                  </div>
                ) : (
                  <textarea
                    value={footerHtml}
                    onChange={(e) => setFooterHtml(e.target.value)}
                    rows={14}
                    spellCheck={false}
                    placeholder={`<hr/>

<div style='font-size:12px;text-align:center;'>

<p>{{CompanyName}}</p>

<p>{{SupportEmail}}</p>

<p>{{CopyrightText}}</p>

<p>
<a href='{{TermsUrl}}'>Terms</a>
|
<a href='{{PrivacyPolicyUrl}}'>Privacy</a>
</p>

</div>`}
                    className="w-full resize-y rounded-xl border border-slate-200 bg-slate-950 px-4 py-3 font-mono text-xs leading-5 text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-primary focus:ring-2 focus:ring-primary/10"
                  />
                )}
              </section>
            </div>

            {/* ------------------------------------------------ */}
            {/* FOOTER ACTIONS */}
            {/* ------------------------------------------------ */}

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8">
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg bg-[#2E7D32] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#256B2A] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={17} className="animate-spin" />

                    {isEdit ? "Saving..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Save size={17} />

                    {isEdit ? "Save Changes" : "Create Layout"}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailLayoutDetailsScreen;
